import * as Factory from 'factory.ts'
import faker from 'faker'

import { PurchaseOrderStatuses } from '@webapp/models/enums'
import { AddressMock } from '@webapp/tests/mocks/address.factory'
import { CurrencyMock } from './currency.factory'
import { PurchaseOrderLineItemV2Mock } from './purchaseOrderLineItemV2.factory'
import { UserMock } from './user.factory'
import { SimpleVendorMock, VendorMock } from './vendor.factory'
import { type PurchaseOrderV2, type PurchaseOrderV3 } from '../../models'

export const PurchaseOrderV3Mock = Factory.Sync.makeFactory<PurchaseOrderV3>({
  buyer_address: Factory.each(() => AddressMock.build()),
  buyer_name: Factory.each(
    () => `${faker.name.firstName()} ${faker.name.lastName()}`
  ),
  buyer_contact: Factory.each(
    () => `${faker.name.firstName()} ${faker.name.lastName()}`
  ),
  buyer_addressLineOne: null,
  buyer_city: null,
  buyer_country: null,
  buyer_postalCode: null,
  buyer_state_province: null,
  PO_Num: '',
  date: Factory.each(() => faker.date.past().toISOString()),
  deposit: null,
  discount: {
    value: 0,
    type: 'amount',
  },
  freight: '0.00',
  fulfilment_status: '',
  id: Factory.each(() => faker.random.number()),
  item_count: 1,
  last_modified: Factory.each(() => faker.date.past().toISOString()),
  other: '0.00',
  pref_vendor: Factory.each(() => VendorMock.build()),
  promise_date: Factory.each(() => faker.date.past().toISOString()),
  punchout_items_rejected: false,
  purchased_currency: Factory.each(() => CurrencyMock.build()),
  purchased_currency_rate: '1.00',
  purchaser: Factory.each(() => UserMock.build()),
  subtotal: Factory.each(() => faker.random.number().toString()),
  total: Factory.each(() => faker.random.number().toString()),
  uuid: Factory.each(() => faker.random.uuid()),
  version: 1,
  state: PurchaseOrderStatuses.REOPEN,
  number: '',
  procurify_PO: Factory.each(() => faker.random.number()),
  punchout_system: null,
  punchout_vendor_reference: null,
  receiver_contact: Factory.each(
    () => `${faker.name.firstName()} ${faker.name.lastName()}`
  ),
  receiver_address: null,
  receiver_addressLineOne: null,
  receiver_city: null,
  receiver_country: null,
  receiver_name: null,
  receiver_postalCode: null,
  receiver_state_province: null,
  creditcard: null,
  creditcard_is_editable: true,
})
  .withDerivation1(['id'], 'number', (id) => {
    return `PF${id}`
  })
  .withDerivation1(['id'], 'procurify_PO', (id) => {
    return id
  })

export const PurchaseOrderV2Mock = Factory.Sync.makeFactory<PurchaseOrderV2>({
  order_items: PurchaseOrderLineItemV2Mock.buildList(1),
  buyer_address: Factory.each(() => AddressMock.build()),
  buyer_name: Factory.each(
    () => `${faker.name.firstName()} ${faker.name.lastName()}`
  ),
  buyer_contact: Factory.each(() => faker.internet.email()),
  PO_Num: '',
  date: Factory.each(() => faker.date.past().toISOString()),
  deposit: '0.00',
  is_deposit_editable: true,
  discount: {
    value: 0,
    type: 'amount',
  },
  freight: '0.00',
  fulfilment_status: '',
  item_count: 1,
  last_modified: Factory.each(() => faker.date.past().toISOString()),
  other: '0.00',
  pref_vendor: Factory.each(() => VendorMock.build()),
  promise_date: Factory.each(() => faker.date.past().toISOString()),
  purchased_currency: Factory.each(() => CurrencyMock.build()),
  purchased_currency_rate: '1.00',
  purchaser: Factory.each(() => UserMock.build()),
  subtotal: Factory.each(() => faker.random.number().toString()),
  total: Factory.each(() => faker.random.number().toString()),
  uuid: Factory.each(() => faker.random.uuid()),
  version: 1,
  state: PurchaseOrderStatuses.REOPEN,
  number: '',
  procurify_PO: Factory.each(() => faker.random.number()),
  punchout_system: null,
  punchout_vendor_reference: null,
  active: true,
  buyer_addressLineOne: null,
  buyer_city: null,
  buyer_country: null,
  buyer_postalCode: null,
  buyer_state_province: null,
  closed_date: null,
  comment: null,
  disclaimer_description: null,
  disclaimer_text: null,
  most_recent_version: 1,
  payment_term: null,
  payment_terms: null,
  purchase_agreement: null,
  purchase_agreement_status: null,
  receiver_address: Factory.each(() => AddressMock.build()),
  receiver_addressLineOne: null,
  receiver_city: null,
  receiver_contact: Factory.each(
    () => `${faker.name.firstName()} ${faker.name.lastName()}`
  ),
  receiver_country: null,
  receiver_name: null,
  receiver_postalCode: null,
  receiver_state_province: null,
  recorded_date: null,
  shipping_method: null,
  shipping_term: null,
  shipping_terms: null,
  staged_for_export: null,
  tax: {
    type: 'amount',
    value: 0,
  },
  vendor_addressLineOne: null,
  vendor_city: null,
  vendor_contact: null,
  vendor_country: null,
  vendor_name: null,
  vendor_postalCode: null,
  vendor_state_province: null,
  custom_fields: [],
  cost_details: {
    subtotal: 100,
    tax_amount: 10,
    total_cost: 110,
    total_cost_in_base_currency: 110,
  },
  creditcard: null,
  creditcard_is_editable: true,
})
  .withDerivation1(
    ['receiver_address'],
    'receiver_addressLineOne',
    (address) => {
      return address.addressLineOne
    }
  )
  .withDerivation1(['receiver_address'], 'receiver_city', (address) => {
    return address.city
  })
  .withDerivation1(['receiver_address'], 'receiver_country', (address) => {
    return address.country
  })
  .withDerivation1(['receiver_address'], 'receiver_name', (address) => {
    return address.name
  })
  .withDerivation1(['receiver_address'], 'receiver_postalCode', (address) => {
    return address.postalCode
  })
  .withDerivation1(
    ['receiver_address'],
    'receiver_state_province',
    (address) => {
      return address.state_province
    }
  )
  .withDerivation1(['buyer_address'], 'buyer_addressLineOne', (address) => {
    return address.addressLineOne
  })
  .withDerivation1(['buyer_address'], 'buyer_city', (address) => {
    return address.city
  })
  .withDerivation1(['buyer_address'], 'buyer_country', (address) => {
    return address.country
  })
  .withDerivation1(['buyer_address'], 'buyer_postalCode', (address) => {
    return address.postalCode
  })
  .withDerivation1(['buyer_address'], 'buyer_state_province', (address) => {
    return address.state_province
  })
